import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    OnInit,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import {
    takeUntilDestroyed,
    toObservable,
    toSignal,
} from '@angular/core/rxjs-interop';
import {
    ButtonComponent,
    IconButtonComponent,
    LoaderComponent,
    ResizeDirective,
} from '@interacta-shared/ui';
import { IconComponent } from '@interacta-shared/ui-icons';

import { ConfirmDialogService } from '@interacta-shared/ui-common';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    distinctUntilChanged,
    filter,
    firstValueFrom,
    interval,
    switchMap,
    takeWhile,
    withLatestFrom,
} from 'rxjs';
import {
    buildAbortedLabel,
    buildBulkWorkflowTransitionBannerTitle,
    buildUpdatedOkLabel,
    buildUpdatedWithErrorLabel,
} from '../../helpers/bulk-workflow-transition.utils';
import { OperationState } from '../../models/bulk-workflow-transition.model';
import {
    abortCurrentOperation,
    fetchLastUnreadOperation,
    goToReport,
    markCurrentOperationAsRead,
} from '../../store/bulk-workflow-transition.actions';
import {
    selectCurrentOperation,
    selectCurrentOperationIsBusy,
} from '../../store/bulk-workflow-transition.selectors';

const ANIMATIONS_DURATION = 150;
/**
 * https://injenia.atlassian.net/wiki/spaces/IISP/pages/4887445505/Transizioni+di+stato+massive#Modalit%C3%A0-di-interazione%3A
 */
const POLLING_INTERVAL_MS = 2000;
@Component({
    selector: 'interacta-bulk-workflow-transition-banner',
    templateUrl: './bulk-workflow-transition-banner.component.html',
    styles: `
        /*
        this class is needed because the string inside the title has numbers inside <b> tags.
        this prevents the numbers from becoming bolder.
        */
        .interacta-banner-title > b {
            font-weight: bold;
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        NgTemplateOutlet,
        IconComponent,
        IconButtonComponent,
        ButtonComponent,
        TranslatePipe,
        LoaderComponent,
        ResizeDirective,
    ],
})
export class BulkWorkflowTransitionBannerComponent implements OnInit {
    private readonly store = inject(Store);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly translateService = inject(TranslateService);

    /**
     * cancelling or marking as read
     */
    isBannerBusy = this.store.selectSignal(selectCurrentOperationIsBusy);

    currentOperation$ = this.store.select(selectCurrentOperation);
    currentOperation = toSignal(this.currentOperation$);

    isBannerVisible = computed(() => !!this.currentOperation());
    currOpState = computed(() => this.currentOperation()?.state);
    currOpStateInProgress = computed(
        () => this.currentOperation()?.state === OperationState.IN_PROGRESS,
    );
    currOpTotalPosts = computed(
        () => this.currentOperation()?.posts.length ?? 0,
    );
    currOpSuccessPostsCount = computed(
        () => this.currentOperation()?.successPostsCount ?? 0,
    );
    currOpFailedPostsCount = computed(
        () => this.currentOperation()?.failedPostsCount ?? 0,
    );
    disableCloseButton = computed(
        () =>
            !this.currOpState() ||
            this.currOpStateInProgress() ||
            this.isBannerBusy(),
    );

    titleLabel = computed(() => {
        const currentOperation = this.currentOperation();
        if (!currentOperation) return null;
        return buildBulkWorkflowTransitionBannerTitle(
            currentOperation,
            this.translateService,
        );
    });

    okLabel = computed(() => {
        const currentOperation = this.currentOperation();
        if (!currentOperation) return null;
        return buildUpdatedOkLabel(currentOperation, this.translateService);
    });

    errorLabel = computed(() => {
        const currentOperation = this.currentOperation();
        if (!currentOperation) return null;
        return buildUpdatedWithErrorLabel(
            currentOperation,
            this.translateService,
        );
    });

    abortedLabel = computed(() => {
        const currentOperation = this.currentOperation();
        if (!currentOperation) return null;
        return buildAbortedLabel(currentOperation, this.translateService);
    });

    isBannerCollapsed = signal(false);
    sectionHeight = signal(0);

    readonly ANIMATIONS_DURATION = ANIMATIONS_DURATION;

    constructor() {
        /**
         * Set the banner not collapsed each time
         * isBannerVisible changes, to reset the inner collapsed
         * status
         */
        toObservable(this.isBannerVisible)
            .pipe(takeUntilDestroyed(), distinctUntilChanged())
            .subscribe(() => this.isBannerCollapsed.set(false));

        /**
         * When an operation is completed,
         * set the banner not collapsed
         */
        this.currentOperation$
            .pipe(
                filter(
                    (op) =>
                        op !== null && op.state !== OperationState.IN_PROGRESS,
                ),
                takeUntilDestroyed(),
            )
            .subscribe(() => {
                this.isBannerCollapsed.set(false);
            });

        this.currentOperation$
            .pipe(
                filter(
                    (op) =>
                        op !== null && op.state === OperationState.IN_PROGRESS,
                ),
                switchMap(() =>
                    interval(POLLING_INTERVAL_MS).pipe(
                        withLatestFrom(this.currentOperation$),
                        takeWhile(
                            ([, operation]) =>
                                operation !== null &&
                                operation.state === OperationState.IN_PROGRESS,
                        ),
                    ),
                ),
                takeUntilDestroyed(),
            )
            .subscribe(() => {
                this.store.dispatch(fetchLastUnreadOperation());
            });
    }

    ngOnInit(): void {
        this.store.dispatch(fetchLastUnreadOperation());
    }

    onCollapseClick(): void {
        this.isBannerCollapsed.set(!this.isBannerCollapsed());
    }

    onCloseClick(): void {
        if (this.currOpState() && !this.currOpStateInProgress()) {
            this.store.dispatch(markCurrentOperationAsRead());
        }
    }

    async abortCurrentOperation(): Promise<void> {
        const confirmed = await firstValueFrom(
            this.confirmDialogService.open({
                title: 'BULK_WORKFLOW_TRANSITION.CONFIRM_DIALOG.TITLE',
                description:
                    'BULK_WORKFLOW_TRANSITION.CONFIRM_DIALOG.DESCRIPTION',
                cancel: 'BULK_WORKFLOW_TRANSITION.CONFIRM_DIALOG.UNDO',
                confirm: 'BULK_WORKFLOW_TRANSITION.CONFIRM_DIALOG.CONFIRM',
            }),
        );

        if (confirmed) {
            if (this.currOpStateInProgress()) {
                this.store.dispatch(abortCurrentOperation());
            }
        }
    }
    onOpenReportClick(): void {
        if (this.currOpState() && !this.currOpStateInProgress()) {
            this.store.dispatch(goToReport());
        }
    }
}
